import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { createReportAppointmentOrders, getAppointmentOrders, getReportById, getChecksByVisitId, getVisitById, getConversationMessagesByVisitId, getPendingChecksByVisitId, submitCheckResult } from '../api/reportApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';

// 添加Markdown样式CSS
const markdownStyles = `
  .markdown-content {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    line-height: 1.6;
    color: #333;
    text-align: left;
  }
  
  .markdown-content h1 {
    color: #2c3e50;
    font-size: 1.8em;
    margin-top: 1.5em;
    margin-bottom: 0.8em;
    padding-bottom: 0.3em;
    border-bottom: 1px solid #eaecef;
    text-align: left;
    font-weight: 700;
  }
  
  .markdown-content h2 {
    color: #3498db;
    font-size: 1.5em;
    margin-top: 1.2em;
    margin-bottom: 0.6em;
    text-align: left;
    font-weight: 600;
  }
  
  .markdown-content h3 {
    color: #2980b9;
    font-size: 1.3em;
    margin-top: 1em;
    margin-bottom: 0.5em;
    text-align: left;
  }
  
  .markdown-content p {
    margin-top: 0.5em;
    margin-bottom: 1em;
    text-align: left;
  }
  
  .markdown-content ul, .markdown-content ol {
    margin-top: 0.5em;
    margin-bottom: 1em;
    padding-left: 2em;
    text-align: left;
  }
  
  .markdown-content li {
    margin-bottom: 0.3em;
    text-align: left;
  }
  
  .markdown-content table {
    border-collapse: collapse;
    width: 100%;
    margin-top: 1em;
    margin-bottom: 1em;
  }
  
  .markdown-content th, .markdown-content td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }
  
  .markdown-content th {
    background-color: #f2f2f2;
    font-weight: bold;
  }
  
  .markdown-content code {
    background-color: #f4f4f4;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
  }
  
  .markdown-content pre {
    background-color: #f4f4f4;
    padding: 10px;
    border-radius: 5px;
    overflow-x: auto;
  }
  
  .markdown-content blockquote {
    border-left: 4px solid #ddd;
    padding-left: 16px;
    margin: 1em 0;
    color: #666;
  }
`;

const ReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');
  const appointmentOrdersSectionRef = useRef(null);
  const focusTarget = useMemo(() => new URLSearchParams(location.search).get('focus'), [location.search]);
  
  const [report, setReport] = useState(null);
  const [visit, setVisit] = useState(null);
  const [reportContent, setReportContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // 检查项相关状态
  const [checks, setChecks] = useState([]);
  const [checksLoading, setChecksLoading] = useState(false);
  const [checksError, setChecksError] = useState('');
  const [expandedChecks, setExpandedChecks] = useState({});

  // 对话消息相关状态
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [showConversations, setShowConversations] = useState(false);

  // 待检查项目相关状态
  const [pendingChecks, setPendingChecks] = useState([]);
  const [pendingChecksLoading, setPendingChecksLoading] = useState(false);
  const [pendingChecksError, setPendingChecksError] = useState('');
  const [appointmentOrders, setAppointmentOrders] = useState([]);
  const [appointmentDrafts, setAppointmentDrafts] = useState([{ check_type: '', description: '' }]);
  const [creatingAppointmentOrders, setCreatingAppointmentOrders] = useState(false);

  // 检查结果输入相关状态
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedPendingCheck, setSelectedPendingCheck] = useState(null);
  const [resultInputType, setResultInputType] = useState('text'); // 'text' or 'select'
  const [resultText, setResultText] = useState('');
  const [resultValue, setResultValue] = useState('');
  const [resultDescription, setResultDescription] = useState('');
  const [submittingResult, setSubmittingResult] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // 按钮样式函数
  const getButtonStyle = (type = 'primary') => {
    const baseStyle = {
      padding: '12px 24px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '500',
      color: 'white',
      transition: 'all 0.3s ease',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      overflow: 'hidden'
    };

    const styles = {
      primary: {
        ...baseStyle,
        color: '#0b1727',
        background: 'linear-gradient(135deg, #cfac67 0%, #43d6c5 100%)',
        boxShadow: '0 12px 28px rgba(179, 143, 91, 0.26)'
      },
      success: {
        ...baseStyle,
        color: '#06231c',
        background: 'linear-gradient(135deg, #43d6c5 0%, #7ae5ba 100%)',
        boxShadow: '0 12px 28px rgba(79, 216, 164, 0.24)'
      },
      secondary: {
        ...baseStyle,
        background: 'linear-gradient(135deg, #173b59 0%, #23697f 100%)',
        boxShadow: '0 12px 28px rgba(16, 32, 51, 0.24)'
      },
      dark: {
        ...baseStyle,
        background: 'linear-gradient(135deg, #102033 0%, #07111d 100%)',
        boxShadow: '0 12px 28px rgba(7, 17, 29, 0.28)'
      }
    };

    return styles[type] || styles.primary;
  };

  const handleButtonHover = (e, isEnter) => {
    if (isEnter) {
      e.target.style.transform = 'translateY(-2px)';
      e.target.style.boxShadow = e.target.style.boxShadow.replace('0.4)', '0.6)');
    } else {
      e.target.style.transform = 'translateY(0)';
      e.target.style.boxShadow = e.target.style.boxShadow.replace('0.6)', '0.4)');
    }
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const data = await getReportById(id);
        setReport(data);
        
        // 判断content是否存在
        if (typeof data.content === 'string' && data.content.trim() !== '') {
          setReportContent(data.content);
        } else if (data.content && typeof data.content === 'object' && Object.keys(data.content).length > 0) {
          setReportContent(JSON.stringify(data.content, null, 2));
        } else {
          setReportContent('');
        }
        
        // 获取相关的检查项、待检查项和对话消息
        if (data.visit_id) {
          fetchChecks(data.visit_id);
          fetchPendingChecks(data.visit_id);
          fetchMessages(data.visit_id);
          fetchAppointmentOrders(data.visit_id);
          
          try {
            const visitData = await getVisitById(data.visit_id);
            setVisit(visitData);
          } catch (visitError) {
            console.error(`获取就诊信息失败:`, visitError);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('获取报告详情失败:', error);
        setError(t('reports.fetchReportError') + ': ' + (error.response?.data?.error || t('common.unknownError')));
        setLoading(false);
      }
    };
    
    fetchReport();
  }, [id, t]);

  // 获取检查项
  const fetchChecks = async (visitId) => {
    if (!visitId) {
      setChecksError(t('reports.noVisitId'));
      setChecksLoading(false);
      return;
    }
    
    try {
      setChecksLoading(true);
      const data = await getChecksByVisitId(visitId);
      setChecks(data || []);
      setChecksError('');
    } catch (error) {
      console.error(`获取就诊ID${visitId}的检查项失败:`, error);
      setChecksError(t('reports.fetchChecksError') + ': ' + (error.response?.data?.error || error.message || t('common.unknownError')));
    } finally {
      setChecksLoading(false);
    }
  };

  // 获取待检查项目列表
  const fetchPendingChecks = async (visitId) => {
    if (!visitId) {
      setPendingChecksError(t('reports.noVisitId'));
      setPendingChecksLoading(false);
      return;
    }

    try {
      setPendingChecksLoading(true);
      const data = await getPendingChecksByVisitId(visitId);
      setPendingChecks(data || []);
      setPendingChecksError('');
    } catch (error) {
      console.error(`获取就诊ID${visitId}的待检查项失败:`, error);
      setPendingChecksError(t('reports.fetchPendingChecksError') + ': ' + (error.response?.data?.error || error.message || t('common.unknownError')));
    } finally {
      setPendingChecksLoading(false);
    }
  };

  const fetchAppointmentOrders = async (visitId) => {
    if (!visitId) {
      setAppointmentOrders([]);
      return;
    }

    try {
      const [sourceOrders, claimOrders] = await Promise.all([
        getAppointmentOrders({ source_visit_id: visitId }),
        getAppointmentOrders({ claim_visit_id: visitId })
      ]);

      const merged = [...(sourceOrders || []), ...(claimOrders || [])];
      const uniqueOrders = merged.filter((order, index, array) => array.findIndex((candidate) => candidate.id === order.id) === index);
      setAppointmentOrders(uniqueOrders);
    } catch (requestError) {
      console.error(`获取就诊ID${visitId}关联预约单失败:`, requestError);
      setAppointmentOrders([]);
    }
  };

  const addAppointmentDraft = () => {
    setAppointmentDrafts((current) => [...current, { check_type: '', description: '' }]);
  };

  const updateAppointmentDraft = (index, field, value) => {
    setAppointmentDrafts((current) =>
      current.map((draft, draftIndex) => (
        draftIndex === index ? { ...draft, [field]: value } : draft
      ))
    );
  };

  const removeAppointmentDraft = (index) => {
    setAppointmentDrafts((current) => {
      if (current.length === 1) {
        return [{ check_type: '', description: '' }];
      }
      return current.filter((_, draftIndex) => draftIndex !== index);
    });
  };

  const handleCreateAppointmentOrders = async () => {
    if (!report?.id) {
      setError(t('reports.fetchReportError'));
      return;
    }

    const appointmentOrdersPayload = appointmentDrafts
      .map((draft) => ({
        check_type: draft.check_type.trim(),
        description: draft.description.trim()
      }))
      .filter((draft) => draft.check_type);

    if (appointmentOrdersPayload.length === 0) {
      setError(t('reports.appointmentOrdersValidationError'));
      return;
    }

    try {
      setCreatingAppointmentOrders(true);
      setError('');
      setSuccessMessage('');

      await createReportAppointmentOrders(report.id, appointmentOrdersPayload);
      await fetchAppointmentOrders(report.visit_id);
      setAppointmentDrafts([{ check_type: '', description: '' }]);
      setSuccessMessage(t('reports.appointmentOrdersCreateSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (requestError) {
      console.error(`为报告ID${report.id}追加预约单失败:`, requestError);
      setError(t('reports.appointmentOrdersCreateError') + ': ' + (requestError.response?.data?.error || requestError.message || t('common.unknownError')));
    } finally {
      setCreatingAppointmentOrders(false);
    }
  };

  // 打开检查结果输入模态框
  const openResultModal = (pendingCheck) => {
    setSelectedPendingCheck(pendingCheck);
    setResultText('');
    setResultValue('');
    setResultDescription('');
    setSubmitError('');
    
    // 根据检查类型设置默认输入类型
    const checkType = pendingCheck.check_type?.toLowerCase();
    if (checkType && (checkType.includes('lab') || checkType.includes('blood') || checkType.includes('quick'))) {
      setResultInputType('select');
      setResultValue('阴性'); // 默认值
    } else {
      setResultInputType('text');
    }
    
    setShowResultModal(true);
  };

  // 关闭检查结果输入模态框
  const closeResultModal = () => {
    setShowResultModal(false);
    setSelectedPendingCheck(null);
    setResultText('');
    setResultValue('');
    setResultDescription('');
    setSubmitError('');
  };

  // 提交检查结果
  const submitResult = async () => {
    if (!selectedPendingCheck) return;
    
    // 验证输入
    if (resultInputType === 'text' && !resultText.trim()) {
      setSubmitError(t('reports.pleaseEnterResult'));
      return;
    }
    
    if (resultInputType === 'select' && !resultValue) {
      setSubmitError(t('reports.pleaseSelectResult'));
      return;
    }

    setSubmittingResult(true);
    setSubmitError('');

    try {
      // 构建CheckItem数组，参考ReportSubmissionView.swift的结构
      const checkItems = [];

      // 添加检查结果（移除不需要的 report_type）
      const finalResult = resultInputType === 'text' ? resultText.trim() : resultValue;
      checkItems.push({
        name: "check_result",
        name_local: selectedPendingCheck.description || t('reports.checkResult'),
        value: finalResult,
        unit: null,
        reference_range: null,
        description: resultDescription.trim() || null,
        description_local: resultDescription.trim() || null
      });

      // 调用API提交结果
      await submitCheckResult({
        type: selectedPendingCheck.check_type || 'other',
        blob: null,
        metadata: checkItems,
        blobType: "application/json",
        pendingCheckId: selectedPendingCheck.id,
        visitId: report.visit_id
      });

      // 提交成功后刷新待检查项目列表和检查项列表
      if (report?.visit_id) {
        await fetchPendingChecks(report.visit_id);
        await fetchChecks(report.visit_id);
      }
      
      closeResultModal();
      
      // 移除 alert，不显示成功消息
      
    } catch (error) {
      console.error('提交检查结果失败:', error);
      setSubmitError(t('reports.submitResultError') + ': ' + (error.response?.data?.error || error.message || t('common.unknownError')));
    } finally {
      setSubmittingResult(false);
    }
  };

  // 导出 Markdown 文件
  const handleExportMarkdown = () => {
    if (!reportContent || reportContent.trim() === '') {
      setError(t('reports.noContentToExport'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      // 创建 Blob 对象
      const blob = new Blob([reportContent], { type: 'text/markdown;charset=utf-8' });
      
      // 创建下载链接
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // 生成文件名：report_[报告ID]_[时间戳].md
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `report_${id}_${timestamp}.md`;
      
      // 触发下载
      document.body.appendChild(link);
      link.click();
      
      // 清理
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSuccessMessage(t('reports.exportSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('导出Markdown文件失败:', error);
      setError(t('reports.exportError'));
      setTimeout(() => setError(''), 3000);
    }
  };

  // 导出 PDF 文件（使用浏览器打印功能）
  const handleExportPDF = () => {
    if (!reportContent || reportContent.trim() === '') {
      setError(t('reports.noContentToExport'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      // 创建一个新窗口用于打印
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setError('无法打开打印窗口，请检查浏览器弹窗设置');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // 渲染Markdown为HTML
      const markdownContent = document.querySelector('.markdown-content');
      const htmlContent = markdownContent ? markdownContent.innerHTML : reportContent;

      // 生成打印页面的HTML
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>报告 #${id} - ${timestamp}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Microsoft YaHei", "微软雅黑";
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 20px auto;
              padding: 20px;
            }
            h1 {
              color: #2c3e50;
              font-size: 1.8em;
              margin-top: 1.5em;
              margin-bottom: 0.8em;
              padding-bottom: 0.3em;
              border-bottom: 1px solid #eaecef;
            }
            h2 {
              color: #3498db;
              font-size: 1.5em;
              margin-top: 1.2em;
              margin-bottom: 0.6em;
            }
            h3 {
              color: #2980b9;
              font-size: 1.3em;
              margin-top: 1em;
              margin-bottom: 0.5em;
            }
            p {
              margin-top: 0.5em;
              margin-bottom: 1em;
            }
            ul, ol {
              margin-top: 0.5em;
              margin-bottom: 1em;
              padding-left: 2em;
            }
            li {
              margin-bottom: 0.3em;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              margin: 1em 0;
            }
            table th {
              background-color: #f8f9fa;
              border: 1px solid #dfe2e5;
              padding: 0.5em 1em;
              font-weight: 600;
            }
            table td {
              border: 1px solid #dfe2e5;
              padding: 0.5em 1em;
            }
            @media print {
              body {
                margin: 0;
                padding: 15mm;
              }
            }
          </style>
        </head>
        <body>
          <h1>病历报告 #${id}</h1>
          <p style="color: #666; font-size: 0.9em;">生成时间: ${new Date().toLocaleString('zh-CN')}</p>
          <hr style="margin: 20px 0; border: none; border-top: 2px solid #eee;">
          ${htmlContent}
        </body>
        </html>
      `);
      printWindow.document.close();

      // 等待内容加载完成后触发打印
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };

      setSuccessMessage('请在打印对话框中选择"保存为PDF"');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('导出PDF失败:', error);
      setError('导出PDF失败: ' + error.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  // 导出 DOCX 文件
  const handleExportDOCX = () => {
    if (!reportContent || reportContent.trim() === '') {
      setError(t('reports.noContentToExport'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      // 将Markdown转换为简单的HTML格式
      let htmlContent = reportContent
        // 标题转换
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        // 粗体和斜体
        .replace(/\*\*\*(.*?)\*\*\*/g, '<b><i>$1</i></b>')
        .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
        .replace(/\*(.*?)\*/g, '<i>$1</i>')
        // 列表
        .replace(/^\* (.*$)/gim, '<li>$1</li>')
        .replace(/^- (.*$)/gim, '<li>$1</li>')
        .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
        // 段落
        .replace(/\n\n/g, '</p><p>')
        // 换行
        .replace(/\n/g, '<br/>');

      // 包装在段落标签中
      htmlContent = '<p>' + htmlContent + '</p>';
      
      // 包装列表项
      htmlContent = htmlContent.replace(/(<li>.*?<\/li>)/gs, (match) => {
        return '<ul>' + match + '</ul>';
      });

      // 创建完整的HTML文档
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const fullHTML = `
        <!DOCTYPE html>
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'>
          <title>报告 #${id}</title>
          <style>
            body { font-family: 'Microsoft YaHei', Arial, sans-serif; line-height: 1.6; }
            h1 { color: #2c3e50; font-size: 24pt; margin-top: 12pt; margin-bottom: 6pt; }
            h2 { color: #3498db; font-size: 18pt; margin-top: 10pt; margin-bottom: 5pt; }
            h3 { color: #2980b9; font-size: 14pt; margin-top: 8pt; margin-bottom: 4pt; }
            p { margin-top: 6pt; margin-bottom: 6pt; }
            ul, ol { margin-top: 6pt; margin-bottom: 6pt; }
            li { margin-bottom: 3pt; }
          </style>
        </head>
        <body>
          <h1>病历报告 #${id}</h1>
          <p style="color: #666; font-size: 10pt;">生成时间: ${new Date().toLocaleString('zh-CN')}</p>
          <hr/>
          ${htmlContent}
        </body>
        </html>
      `;

      // 创建Blob对象
      const blob = new Blob(['\ufeff', fullHTML], {
        type: 'application/msword'
      });

      // 创建下载链接
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${id}_${timestamp}.doc`;

      // 触发下载
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccessMessage('报告已导出为Word文档格式');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('导出DOCX失败:', error);
      setError('导出Word文档失败: ' + error.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  // 获取对话消息
  const fetchMessages = async (visitId) => {
    if (!visitId) {
      setMessagesError(t('reports.noVisitId'));
      setMessagesLoading(false);
      return;
    }

    try {
      setMessagesLoading(true);
      const data = await getConversationMessagesByVisitId(visitId);
      
      // 过滤掉以"人类医生已确认如下报告"开头的消息
      const filteredMessages = (data.messages || []).filter(message => {
        return !message.content || !message.content.startsWith('人类医生已确认如下报告');
      });
      
      setMessages(filteredMessages);
      setMessagesError('');
    } catch (error) {
      console.error(`获取就诊ID${visitId}的对话消息失败:`, error);
      setMessagesError(t('reports.fetchConversationError') + ': ' + (error.response?.data?.error || error.message || t('common.unknownError')));
    } finally {
      setMessagesLoading(false);
    }
  };

  // 格式化对话消息时间
  const formatConversationTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // 检查消息是否包含真正的工具调用XML标签（排除记录性标签）
  const hasToolCallXML = (content) => {
    if (!content) return false;
    // 只有这些标签才是真正的工具调用
    const toolCallPatterns = [
      /<exam_order>/,
      /<long_exam_order>/,
      /<exam_pending>/,
      /<consultation_finish>/
    ];
    return toolCallPatterns.some(pattern => pattern.test(content));
  };

  // 检查消息是否包含记录性XML标签
  const hasRecordingXML = (content) => {
    if (!content) return false;
    const recordingPatterns = [
      /<subjective>/,
      /<objective>/,
      /<assessment>/,
      /<plan>/,
      /<diagnosis>/,
      /<treatment>/,
      /<end>/
    ];
    return recordingPatterns.some(pattern => pattern.test(content));
  };

  // 清理消息内容，移除TTS标签但保留内容，移除universal_medical标签及其内容
  const cleanMessageContent = (content) => {
    if (!content) return content;
    
    let cleanedContent = content;
    
    // 移除 <tts> 标签但保留其内容
    cleanedContent = cleanedContent.replace(/<tts>(.*?)<\/tts>/g, '$1');
    
    // 完全移除 <universal_medical> 标签及其内容
    cleanedContent = cleanedContent.replace(/<universal_medical[^>]*>[\s\S]*?<\/universal_medical>/g, '');
    
    // 清理多余的换行符
    cleanedContent = cleanedContent.replace(/\n\s*\n\s*\n/g, '\n\n').trim();
    
    return cleanedContent;
  };

  // 格式化工具消息内容，提供国际化支持
  const formatToolMessageContent = (content) => {
    if (!content) return content;

    // 首先清理消息内容，移除不需要显示的XML标签
    let translatedContent = cleanMessageContent(content);

    // 处理检查类型相关的消息
    if (translatedContent.includes('检查类型：')) {
      translatedContent = translatedContent.replace(/检查类型：\s*(\w+)/g, (match, checkType) => {
        return `${t('reports.checkType')}: ${checkType}`;
      });
    }

    if (translatedContent.includes('检查结果明细：')) {
      translatedContent = translatedContent.replace(/检查结果明细：/g, t('reports.checkResultDetails'));
    }

    // 处理检查项目名称的翻译
    const examMappings = {
      '快速链球菌检测': t('reports.rapidStrepTest'),
      '全血细胞计数+分类计数': t('reports.completeBloodCountDiff'),
      '肺炎支原体抗体检测': t('reports.mycoplasmaPneumoniaeTest'),
      '血常规+C反应蛋白': t('reports.bloodCountCReactiveProtein'),
      '流感病毒抗原检测': t('reports.influenzaVirusAntigenTest'),
      '新冠病毒抗原检测': t('reports.covidAntigenTest'),
      '六项呼吸道病原体核酸检测': t('reports.respiratoryPathogenNucleicAcid'),
      '尿液分析': t('reports.urineAnalysis'),
      '粪常规': t('reports.fecalRoutine'),
      '腹部触诊': t('reports.abdominalPalpation'),
      '体温测量': t('reports.temperatureMeasurement'),
      '血压测量': t('reports.bloodPressureMeasurement'),
      '心肺听诊': t('reports.cardiopulmonaryAuscultation'),
      '咽部检查': t('reports.throatExamination')
    };

    // 替换检查项目名称
    Object.entries(examMappings).forEach(([chinese, translation]) => {
      // 转义正则表达式特殊字符，特别是加号
      const escapedChinese = chinese.replace(/[+\-\[\]{}()*?.,\\^$|#\s]/g, '\\$&');
      const regex = new RegExp(escapedChinese, 'g');
      translatedContent = translatedContent.replace(regex, translation);
    });

    // 处理其他常见的中文短语
    const commonPhrases = {
      'consent to the examination': t('reports.consentToExamination'),
      '同意检查': t('reports.consentToExamination'),
      '检查同意': t('reports.consentToExamination'),
      'Positive': t('reports.positive'),
      'Negative': t('reports.negative'),
      'Normal': t('reports.normal'),
      'Abnormal': t('reports.abnormal'),
      '阳性': t('reports.positive'),
      '阴性': t('reports.negative'),
      '正常': t('reports.normal'),
      '异常': t('reports.abnormal'),
      '问诊结束': t('reports.consultationFinish')
    };

    Object.entries(commonPhrases).forEach(([phrase, translation]) => {
      const regex = new RegExp(phrase, 'gi');
      translatedContent = translatedContent.replace(regex, translation);
    });

    return translatedContent;
  };

  // 获取结束标题
  const getEndTitle = (text) => {
    const endMappings = {
      '问诊结束': t('reports.consultationFinish')
    };

    if (endMappings[text]) {
      return endMappings[text];
    }
  };

  // 检查项目中英文映射字典
  const getExamTitle = (text) => {
    const examMappings = {
      '快速链球菌检测': t('reports.rapidStrepTest'),
      '全血细胞计数+分类计数': t('reports.completeBloodCountDiff'),
      '肺炎支原体抗体检测': t('reports.mycoplasmaPneumoniaeTest'),
      '血常规+C反应蛋白': t('reports.bloodCountCReactiveProtein'),
      '流感病毒抗原检测': t('reports.influenzaVirusAntigenTest'),
      '新冠病毒抗原检测': t('reports.covidAntigenTest'),
      '六项呼吸道病原体核酸检测': t('reports.respiratoryPathogenNucleicAcid'),
      '尿液分析': t('reports.urineAnalysis'),
      '粪常规': t('reports.fecalRoutine'),
      '腹部触诊': t('reports.abdominalPalpation'),
      '体温测量': t('reports.temperatureMeasurement'),
      '血压测量': t('reports.bloodPressureMeasurement'),
      '心肺听诊': t('reports.cardiopulmonaryAuscultation'),
      '咽部检查': t('reports.throatExamination')
    };

    // 如果有完全匹配，返回翻译
    if (examMappings[text]) {
      return examMappings[text];
    }

    // 如果没有完全匹配，尝试关键词匹配
    const keywordDict = {
      '快速链球菌检测': ['快速', '链球菌'],
      '全血细胞计数+分类计数': ['全血', '细胞', '计数', '分类'],
      '肺炎支原体抗体检测': ['肺炎', '支原体', '抗体'],
      '血常规+C反应蛋白': ['血常规', 'C反应蛋白'],
      '流感病毒抗原检测': ['流感'],
      '新冠病毒抗原检测': ['新冠'],
      '六项呼吸道病原体核酸检测': ['呼吸道', '病原体', '核酸'],
      '尿液分析': ['尿液', '分析'],
      '粪常规': ['粪'],
      '腹部触诊': ['腹'],
      '体温测量': ['体', '温'],
      '血压测量': ['血', '压'],
      '心肺听诊': ['心', '肺', '听'],
      '咽部检查': ['咽', '喉'],
    };

    const matchedExam = Object.entries(keywordDict).find(([key, keywords]) =>
      keywords.some(keyword => text.includes(keyword))
    );

    if (matchedExam && examMappings[matchedExam[0]]) {
      return examMappings[matchedExam[0]];
    }

    // 如果都没有匹配，返回原文
    return text;
  };

  // 提取和格式化工具调用内容（只处理真正的工具调用标签）
  const formatToolCallContent = (content) => {
    if (!content) return content;

    // 先清理TTS标签，然后提取XML标签内容并格式化
    const cleanedContent = cleanMessageContent(content);
    return cleanedContent
      .replace(/<exam_order>(.*?)<\/exam_order>/g, (match, examText) => `🔬 ${t('reports.examRequest')}: ${getExamTitle(examText)}`)
      .replace(/<long_exam_order>(.*?)<\/long_exam_order>/g, (match, examText) => `🔬 ${t('reports.detailedExamRequest')}: ${getExamTitle(examText)}`)
      .replace(/<exam_pending>(.*?)<\/exam_pending>/g, (match, examText) => `⏳ ${t('reports.examPending')}: ${getExamTitle(examText)}`)
      .replace(/<consultation_finish>(.*?)<\/consultation_finish>/g, (match, text) => `✅ ${t('reports.consultationFinish')}: ${getEndTitle(text)}`);
  };

  // 格式化记录性标签内容（用于在助手消息中突出显示）
  const formatRecordingContent = (content) => {
    if (!content) return content;

    const cleanedContent = cleanMessageContent(content);
    
    // 首先提取所有的XML标签内容
    let result = cleanedContent;
    const xmlReplacements = [
      { pattern: /<subjective>(.*?)<\/subjective>/gs, replacement: (match, text) => `<div style="margin: 4px 0 2px 0; padding: 8px; background-color: rgba(76, 175, 80, 0.1); border-left: 3px solid #4CAF50; border-radius: 4px;"><strong>📝 ${t('reports.subjectiveSymptom')}:</strong><br/>${text.trim()}</div>` },
      { pattern: /<objective>(.*?)<\/objective>/gs, replacement: (match, text) => `<div style="margin: 2px 0; padding: 8px; background-color: rgba(33, 150, 243, 0.1); border-left: 3px solid #2196F3; border-radius: 4px;"><strong>🔍 ${t('reports.objectiveExam')}:</strong><br/>${text.trim()}</div>` },
      { pattern: /<assessment>(.*?)<\/assessment>/gs, replacement: (match, text) => `<div style="margin: 2px 0; padding: 8px; background-color: rgba(255, 193, 7, 0.1); border-left: 3px solid #FFC107; border-radius: 4px;"><strong>💡 ${t('reports.assessment')}:</strong><br/>${text.trim()}</div>` },
      { pattern: /<plan>(.*?)<\/plan>/gs, replacement: (match, text) => `<div style="margin: 2px 0; padding: 8px; background-color: rgba(156, 39, 176, 0.1); border-left: 3px solid #9C27B0; border-radius: 4px;"><strong>📋 ${t('reports.plan')}:</strong><br/>${text.trim()}</div>` },
      { pattern: /<diagnosis>(.*?)<\/diagnosis>/gs, replacement: (match, text) => `<div style="margin: 2px 0; padding: 8px; background-color: rgba(244, 67, 54, 0.1); border-left: 3px solid #F44336; border-radius: 4px;"><strong>🏥 ${t('reports.diagnosis')}:</strong><br/>${text.trim()}</div>` },
      { pattern: /<treatment>(.*?)<\/treatment>/gs, replacement: (match, text) => `<div style="margin: 2px 0; padding: 8px; background-color: rgba(0, 188, 212, 0.1); border-left: 3px solid #00BCD4; border-radius: 4px;"><strong>💊 ${t('reports.treatment')}:</strong><br/>${text.trim()}</div>` },
      { pattern: /<end>(.*?)<\/end>/gs, replacement: () => '' }
    ];

    xmlReplacements.forEach(({ pattern, replacement }) => {
      result = result.replace(pattern, replacement);
    });

    // 清理卡片周围的换行符，确保卡片之间紧密相连
    result = result
      .replace(/\n+(<div[^>]*style="margin: [^"]*"[^>]*>)/g, '\n$1') // 卡片前只保留一个换行
      .replace(/(<\/div>)\n+(<div[^>]*style="margin: [^"]*"[^>]*>)/g, '$1$2') // 相邻卡片之间移除换行
      .replace(/\n{2,}/g, '\n') // 清理其他多余换行
      .trim();

    return result;
  };

  // 渲染对话消息
  const renderConversationMessage = (message, index) => {
    const isUser = message.role === 'user';
    const isTool = message.role === 'tool';
    const isAssistant = message.role === 'assistant';
    const hasXMLTags = hasToolCallXML(message.content);

    // 如果是tool消息，使用特殊布局
    if (isTool) {
      return (
        <div key={index} style={{
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{
            maxWidth: '80%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {/* 系统消息时间戳 */}
            <div style={{
              fontSize: '12px',
              color: '#999',
              marginBottom: '8px',
              fontWeight: '500'
            }}>
              {t('reports.systemMessage')} | {formatConversationTime(message.created_at)}
            </div>

            {/* 系统消息气泡 */}
            <div style={{
              padding: '10px 16px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(108, 117, 125, 0.1) 0%, rgba(73, 80, 87, 0.15) 100%)',
              color: '#6c757d',
              border: '1px solid rgba(108, 117, 125, 0.2)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
              fontSize: '14px',
              lineHeight: '1.4',
              textAlign: 'center',
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M9 9h6v6H9z" fill="currentColor" />
                <path d="M9 1v4M15 1v4M9 19v4M15 19v4M1 9h4M1 15h4M19 9h4M19 15h4" stroke="currentColor" strokeWidth="2" />
              </svg>
              {formatToolMessageContent(message.content)}
            </div>
          </div>
        </div>
      );
    }

    // 如果是包含真正工具调用XML标签的助手消息，需要特殊处理
    if (isAssistant && hasXMLTags) {
      // 分离普通文本和工具调用XML标签内容
      const lines = message.content.split('\n');
      const normalText = [];
      const toolCallLines = [];

      lines.forEach(line => {
        if (hasToolCallXML(line)) {
          toolCallLines.push(line);
        } else if (line.trim()) {
          normalText.push(line);
        }
      });

      return (
        <div key={index} style={{ marginBottom: '20px' }}>
          {/* 普通助手消息部分 */}
          {normalText.length > 0 && (
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: '12px',
              marginBottom: '10px'
            }}>
              {/* 助手头像 */}
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #43d6c5 0%, #7ae5ba 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                flexShrink: 0
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="11" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
                  <circle cx="12" cy="5" r="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 7v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="9" cy="16" r="1" fill="currentColor" />
                  <circle cx="15" cy="16" r="1" fill="currentColor" />
                </svg>
              </div>

              {/* 助手消息内容 */}
              <div style={{
                maxWidth: '70%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '6px',
                  fontWeight: '500'
                }}>
                  {t('reports.assistantMessage')} | {formatConversationTime(message.created_at)}
                </div>

                <div style={{
                  padding: '12px 16px',
                  borderRadius: '18px 18px 18px 4px',
                  background: 'rgba(250, 245, 236, 0.94)',
                  color: '#333',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  wordBreak: 'break-word',
                  lineHeight: '1.5',
                  textAlign: 'left'
                }}>
                  <div style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}
                  dangerouslySetInnerHTML={{
                    __html: formatRecordingContent(normalText.join('\n')).replace(/\n/g, '<br/>')
                  }}>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 工具调用XML标签部分（只有真正的工具调用才显示） */}
          {toolCallLines.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: normalText.length > 0 ? '5px' : '0'
            }}>
              <div style={{
                maxWidth: '80%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#999',
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  {t('reports.toolCallMessage')}
                </div>

                <div style={{
                  padding: '10px 16px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.15) 100%)',
                  color: '#856404',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  textAlign: 'left',
                  whiteSpace: 'pre-wrap'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                    </svg>
                    <div>{formatToolCallContent(toolCallLines.join('\n'))}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={index} style={{
        marginBottom: '20px',
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: '12px'
      }}>
        {/* 头像 */}
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: isUser
            ? 'linear-gradient(135deg, #cfac67 0%, #43d6c5 100%)'
            : 'linear-gradient(135deg, #43d6c5 0%, #7ae5ba 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '14px',
          fontWeight: '600',
          flexShrink: 0
        }}>
          {isUser ? (
            // 用户头像 - 简笔画人物
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="8" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            // 助手头像 - 简笔画机器人
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="11" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
              <circle cx="12" cy="5" r="2" stroke="currentColor" strokeWidth="2" />
              <path d="M12 7v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <circle cx="9" cy="16" r="1" fill="currentColor" />
              <circle cx="15" cy="16" r="1" fill="currentColor" />
            </svg>
          )}
        </div>

        {/* 消息内容 */}
        <div style={{
          maxWidth: '70%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start'
        }}>
          {/* 消息头部信息 */}
          <div style={{
            fontSize: '12px',
            color: '#666',
            marginBottom: '6px',
            display: 'flex',
            flexDirection: isUser ? 'row-reverse' : 'row',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontWeight: '500' }}>
              {isUser ? t('reports.userMessage') : t('reports.assistantMessage')}
            </span>
            {message.name && isUser && (
              <>
                <span>|</span>
                <span>{message.name}</span>
              </>
            )}
            {message.age && isUser && (
              <>
                <span>|</span>
                <span>{message.age}</span>
              </>
            )}
            {message.sex && isUser && (
              <>
                <span>|</span>
                <span>{message.sex}</span>
              </>
            )}
            <span>|</span>
            <span>{formatConversationTime(message.created_at)}</span>
          </div>

          {/* 消息气泡 */}
          <div style={{
            padding: '12px 16px',
            borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            background: isUser
              ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)'
              : 'rgba(255, 255, 255, 0.95)',
            color: isUser ? 'white' : '#333',
            border: isUser ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            wordBreak: 'break-word',
            lineHeight: '1.5',
            textAlign: 'left'
          }}>
                      <div style={{
            whiteSpace: 'pre-wrap',
            textAlign: 'left'
          }} 
          dangerouslySetInnerHTML={{
            __html: hasRecordingXML(message.content) 
              ? formatRecordingContent(message.content).replace(/\n/g, '<br/>')
              : cleanMessageContent(message.content).replace(/\n/g, '<br/>')
          }}>
          </div>
          </div>
        </div>
      </div>
    );
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return t('reports.notSpecified');
    const date = new Date(dateString);
    return date.toLocaleString(isZh ? 'zh-CN' : 'en-US');
  };

  // 格式化报告类型显示
  const formatReportType = (reportType) => {
    if (!reportType) return '';
    
    const typeMap = {
      'proposal': t('reports.proposalReport'),
      'final': t('reports.finalReport')
    };
    
    return typeMap[reportType] || reportType;
  };

  const formatVisitType = (visitType) => {
    if (visitType === 'follow_up') {
      return isZh ? '复诊' : 'Follow-up';
    }
    if (visitType === 'initial') {
      return isZh ? '初诊' : 'Initial';
    }
    if (visitType === 'checkup') {
      return isZh ? '体检' : 'Checkup';
    }
    return isZh ? '未标记' : 'Unspecified';
  };

  const formatRelatedVisits = (relatedVisitIds) => {
    if (!relatedVisitIds || relatedVisitIds.length === 0) {
      return isZh ? '无关联来源 Visit' : 'No linked source visits';
    }

    return relatedVisitIds.map((visitId) => `#${visitId}`).join(' / ');
  };

  const formatClinicLabel = (name, clinicId) => {
    if (name) {
      return name;
    }
    if (clinicId) {
      return `Clinic #${clinicId}`;
    }
    return isZh ? '未注明诊所' : 'Unknown clinic';
  };

  const buildRelatedVisitLink = (visitId) => `/reviewed-reports?visit_id=${visitId}`;
  const buildAppointmentQueueLink = (orderId) => `/appointment-orders?order_id=${orderId}`;
  const canCreateAppointmentOrders = report?.report_type === 'final' && report?.status === 'approved' && (visit?.status === 'complete' || visit?.status === 'completed');
  const showAppointmentOrdersSection = canCreateAppointmentOrders || appointmentOrders.length > 0;

  useEffect(() => {
    if (focusTarget !== 'appointment-orders' || !showAppointmentOrdersSection || !appointmentOrdersSectionRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      appointmentOrdersSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [focusTarget, showAppointmentOrdersSection]);

  // 获取检查类型显示名称
  const getCheckTypeDisplay = (type) => {
    if (!type) return t('common.unknown');
    
    // 处理 CHECKTYPES.XXX 格式的类型
    let normalizedType = type;
    if (type.startsWith('CHECKTYPES.')) {
      const typeKey = type.replace('CHECKTYPES.', '').toLowerCase();
      // 映射常见的检查类型
      const typeMapping = {
        'lab': 'blood',
        'quick': 'other',
        'pure_throat': 'throat',
        'physical': 'common',
        'vital_signs': 'vital_signs',
        'urine': 'urine',
        'blood': 'blood',
        'ct': 'ct',
        'xray': 'xray',
        'ultrasound': 'ultrasound',
        'ecg': 'ecg',
        'mri': 'mri',
        'pathology': 'pathology',
        'endoscopy': 'endoscopy'
      };
      normalizedType = typeMapping[typeKey] || typeKey;
    }
    
    return t(`checkTypes.${normalizedType}`) || type;
  };

  // 切换检查项详情显示
  const toggleCheckDetails = (index) => {
    const detailsElement = document.getElementById(`check-details-${index}`);
    if (detailsElement) {
      if (detailsElement.style.display === 'none' || !detailsElement.style.display) {
        detailsElement.style.display = 'block';
        setExpandedChecks(prev => ({ ...prev, [index]: true }));
      } else {
        detailsElement.style.display = 'none';
        setExpandedChecks(prev => ({ ...prev, [index]: false }));
      }
    }
  };

  // 渲染检查项元数据
  const renderCheckMetadata = (check) => {
    // 检查是否有图片文件
    const hasImage = check.file_url && check.file_mime_type && 
                    (check.file_mime_type.startsWith('image/') || 
                     ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(check.file_mime_type.toLowerCase()));
    
    // 检查是否有音频文件
    const hasAudio = check.file_url && check.file_mime_type && 
                    (check.file_mime_type.startsWith('audio/') || 
                     ['audio/aac', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/mpeg'].includes(check.file_mime_type.toLowerCase()));
    
    // 处理metadata
    let metadataItems = [];
    if (check.metadata && Array.isArray(check.metadata)) {
      metadataItems = check.metadata;
    } else if (check.metadata && typeof check.metadata === 'object') {
      metadataItems = Object.entries(check.metadata).map(([key, value]) => ({
        name: key,
        value: value
      }));
    }
    
    const hasMetadata = metadataItems.length > 0;
    
    // 如果既没有图片、音频也没有metadata，显示无数据
    if (!hasImage && !hasAudio && !hasMetadata) {
      return <p style={{ color: '#334255' }}>{t('reports.noMetadata')}</p>;
    }
    
    return (
      <div className="check-metadata">
        {/* 显示图片 */}
        {hasImage && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>{t('reports.checkImage')}</h4>
            <div style={{
              border: '2px solid #dde6e9',
              borderRadius: '8px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              textAlign: 'center'
            }}>
              <img 
                src={check.file_url} 
                alt={`${getCheckTypeDisplay(check.check_type)} 检查图片`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{ display: 'none', color: '#556476', marginTop: '10px' }}>
                {t('reports.imageLoadError')}
              </div>
              <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#556476' }}>
                {t('reports.fileType')}: {check.file_mime_type}
              </div>
            </div>
          </div>
        )}
        
        {/* 显示音频 */}
        {hasAudio && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>{t('reports.checkAudio')}</h4>
            <div style={{
              border: '2px solid #dde6e9',
              borderRadius: '8px',
              padding: '15px',
              backgroundColor: '#f8f9fa'
            }}>
              <audio 
                controls 
                style={{
                  width: '100%',
                  marginBottom: '10px'
                }}
                preload="metadata"
              >
                <source src={check.file_url} type={check.file_mime_type} />
                {t('reports.audioNotSupported')}
              </audio>
              <div style={{ fontSize: '0.9em', color: '#556476' }}>
                {t('reports.fileType')}: {check.file_mime_type}
              </div>
              <div style={{ fontSize: '0.9em', color: '#556476', marginTop: '5px' }}>
                <a 
                  href={check.file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    color: '#1d6a86',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  {t('reports.downloadAudio')}
                </a>
              </div>
            </div>
          </div>
        )}
        
        {/* 显示metadata */}
        {hasMetadata && (
          <div>
            <h4 style={{ color: '#14253a' }}>{t('reports.checkItemData')}</h4>
            <table className="paper-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '8px', 
                    backgroundColor: '#eef7fa', 
                    border: '1px solid #dde6e9',
                    fontWeight: '600'
                  }}>
                    {t('reports.name')}
                  </th>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '8px', 
                    backgroundColor: '#eef7fa', 
                    border: '1px solid #dde6e9',
                    fontWeight: '600'
                  }}>
                    {t('reports.value')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {metadataItems.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ 
                      padding: '8px', 
                      border: '1px solid #dde6e9'
                    }}>
                      {item.name_local || item.name}
                    </td>
                    <td style={{ 
                      padding: '8px', 
                      border: '1px solid #dde6e9'
                    }}>
                      {item.value !== undefined && item.value !== null
                        ? <span>
                            {String(item.value)}
                            {item.unit ? ` ${item.unit}` : ''}
                            {item.reference_range ? ` (${t('reports.referenceRange')}: ${item.reference_range})` : ''}
                          </span>
                        : t('reports.noData')
                      }
                      {item.description_local || item.description
                        ? <div style={{ marginTop: '5px', color: '#556476', fontSize: '0.9em' }}>
                            {item.description_local || item.description}
                          </div>
                        : null
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <style>{markdownStyles}</style>
        <div style={{
          width: '60px',
          height: '60px',
          border: '3px solid #f3f3f3',
          borderRadius: '50%',
          borderTopColor: '#667eea',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <p style={{ fontSize: '1.2rem', color: '#666' }}>{t('common.loading')}</p>
        
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'transparent',
      padding: '0px',
      position: 'relative'
    }}>
      <style dangerouslySetInnerHTML={{ __html: `
        ${markdownStyles}
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      ` }} />
      
      {/* Back按钮区域 - 左上角 */}
      <div style={{ 
        position: 'absolute', 
        top: '0px', 
        left: '0px', 
        zIndex: 1000 
      }}>
        <button 
          onClick={() => navigate('/reviewed-reports')}
          style={getButtonStyle('dark')}
          onMouseEnter={(e) => handleButtonHover(e, true)}
          onMouseLeave={(e) => handleButtonHover(e, false)}
        >
          {t('common.back')}
        </button>
      </div>
      
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto',
        padding: '0 20px',
        paddingTop: '60px'
      }}>

        {/* 页面标题 */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          marginBottom: '30px',
          background: 'rgba(250, 245, 236, 0.94)',
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h2 style={{ 
              margin: 0,
              fontSize: '2rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #43d6c5 0%, #7ae5ba 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {t('reports.viewReport')} #{id}
            </h2>
            {report && report.report_type && (
              <span style={{
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
                background: report.report_type === 'proposal' 
                  ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(46, 204, 113, 0.1) 0%, rgba(39, 174, 96, 0.1) 100%)',
                color: report.report_type === 'proposal' ? '#667eea' : '#27ae60',
                border: report.report_type === 'proposal' 
                  ? '1px solid rgba(102, 126, 234, 0.3)' 
                  : '1px solid rgba(46, 204, 113, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {formatReportType(report.report_type)}
              </span>
            )}
          </div>
        </div>
        
        {error && (
          <div style={{ 
            background: 'rgba(231, 76, 60, 0.1)',
            border: '1px solid rgba(231, 76, 60, 0.3)',
            color: '#e74c3c',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{ 
            background: 'rgba(46, 204, 113, 0.1)',
            border: '1px solid rgba(46, 204, 113, 0.3)',
            color: '#27ae60',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {successMessage}
          </div>
        )}

        {/* 报告信息 */}
        {report && (
          <div style={{
            background: 'rgba(250, 245, 236, 0.94)',
            borderRadius: '16px',
            padding: '30px',
            marginBottom: '20px',
            boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
            backdropFilter: 'blur(18px)'
          }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: '20px',
              color: '#2c3e50',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              {t('reports.reportInfo')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <p style={{ margin: '8px 0', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.reportId')}:</strong> 
                <span style={{ marginLeft: '8px', color: '#34495e' }}>{report.id}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.status')}:</strong> 
                <span style={{ 
                  marginLeft: '8px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600',
                  ...(report.status === 'pending' ? {
                    background: 'rgba(241, 196, 15, 0.1)',
                    color: '#f39c12',
                    border: '1px solid rgba(241, 196, 15, 0.3)'
                  } : report.status === 'approved' ? {
                    background: 'rgba(46, 204, 113, 0.1)',
                    color: '#27ae60',
                    border: '1px solid rgba(46, 204, 113, 0.3)'
                  } : {
                    background: 'rgba(231, 76, 60, 0.1)',
                    color: '#e74c3c',
                    border: '1px solid rgba(231, 76, 60, 0.3)'
                  })
                }}>
                  {t(`reports.${report.status}`)}
                </span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.clinicId')}:</strong> 
                <span style={{ marginLeft: '8px', color: '#34495e' }}>{report.clinic_id || t('reports.notSpecified')}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.clinicName')}:</strong> 
                <span style={{ marginLeft: '8px', color: '#34495e' }}>{report.clinic_name || t('reports.notSpecified')}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.visitId')}:</strong> 
                <span style={{ marginLeft: '8px', color: '#34495e' }}>{report.visit_id}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{isZh ? '就诊类型' : 'Visit type'}:</strong> 
                <span style={{ marginLeft: '8px', color: '#34495e' }}>{formatVisitType(report.visit_type)}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.createdAt')}:</strong> 
                <span style={{ marginLeft: '8px', color: '#34495e' }}>{formatDate(report.created_at)}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{isZh ? '关联来源' : 'Related visits'}:</strong> 
                <span style={{ marginLeft: '8px', color: '#34495e' }}>{formatRelatedVisits(report.related_visit_ids)}</span>
              </p>
              {report.related_visit_ids?.length > 0 && (
                <div className="visit-chip-list" style={{ paddingLeft: '15px', marginTop: '2px' }}>
                  {report.related_visit_ids.map((visitId) => (
                    <Link key={`report-view-related-${visitId}`} to={buildRelatedVisitLink(visitId)} className="visit-chip">
                      #{visitId}
                    </Link>
                  ))}
                </div>
              )}
              <p style={{ margin: '8px 0', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.reviewer')}:</strong> 
                <span style={{ marginLeft: '8px', color: '#34495e' }}>{report.reviewer_name || t('reports.notReviewed')}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.reviewedAt')}:</strong> 
                <span style={{ marginLeft: '8px', color: '#34495e' }}>{formatDate(report.reviewed_at)}</span>
              </p>
            </div>
            {report.review_note && (
              <div style={{ marginTop: '20px', textAlign: 'left', paddingLeft: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.reviewNote')}:</strong> 
                <span style={{ marginLeft: '8px', color: '#34495e' }}>{report.review_note}</span>
              </div>
            )}

            {showAppointmentOrdersSection && (
              <div ref={appointmentOrdersSectionRef} style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <div style={{ display: 'grid', gap: '0.55rem', marginBottom: '16px' }}>
                  <strong style={{ color: '#2c3e50', fontSize: '1rem' }}>
                    {t('reports.appointmentOrdersSectionTitle')}
                  </strong>
                  <span style={{ color: '#556476', fontSize: '14px', lineHeight: 1.6 }}>
                    {canCreateAppointmentOrders
                      ? t('reports.appointmentOrdersCreateHint')
                      : t('reports.appointmentOrdersReadOnlyHint')}
                  </span>
                </div>

                {appointmentOrders.length > 0 ? (
                  <div className="mini-card-grid">
                    {appointmentOrders.map((order) => (
                      <div
                        key={order.id}
                        className="mini-card"
                        style={{ background: 'rgba(255, 255, 255, 0.55)', borderColor: 'rgba(0, 0, 0, 0.08)' }}
                      >
                        <div className="mini-card__header">
                          <span className="mini-card__title" style={{ color: '#14253a' }}>
                            {order.description || order.check_type}
                          </span>
                          <span style={{ color: '#667085', fontSize: '13px' }}>#{order.id}</span>
                        </div>
                        <div className="mini-card__meta" style={{ color: '#556476' }}>
                          {isZh ? '来源诊所' : 'Source clinic'}: {formatClinicLabel(order.source_clinic_name, order.source_clinic_id)}
                        </div>
                        <div className="mini-card__meta" style={{ color: '#556476' }}>
                          {isZh ? '接诊诊所' : 'Claim clinic'}: {formatClinicLabel(order.claim_clinic_name, order.claim_clinic_id)}
                        </div>
                        <div className="inline-link-row" style={{ marginTop: '0.15rem' }}>
                          <Link className="inline-link" to={buildAppointmentQueueLink(order.id)}>
                            {isZh ? '在预约队列中查看' : 'Open in queue'}
                          </Link>
                          <Link className="inline-link" to={buildRelatedVisitLink(order.source_visit_id)}>
                            {isZh ? '查看来源 Visit' : 'Open source visit'}
                          </Link>
                          {order.source_report_id && (
                            <Link className="inline-link" to={`/reports/${order.source_report_id}/view`}>
                              {isZh ? '查看来源报告' : 'Open source report'}
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '16px 18px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.46)',
                    color: '#556476',
                    border: '1px dashed rgba(0, 0, 0, 0.12)'
                  }}>
                    {t('reports.noAppointmentOrders')}
                  </div>
                )}

                {canCreateAppointmentOrders && (
                  <div style={{
                    marginTop: '18px',
                    padding: '20px',
                    borderRadius: '14px',
                    background: 'rgba(255, 255, 255, 0.56)',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}>
                    <div style={{ display: 'grid', gap: '0.45rem', marginBottom: '16px' }}>
                      <strong style={{ color: '#2c3e50', fontSize: '0.98rem' }}>
                        {t('reports.appointmentOrdersCreateTitle')}
                      </strong>
                      <span style={{ color: '#667085', fontSize: '13px', lineHeight: 1.6 }}>
                        {t('reports.appointmentOrdersCreateDescription')}
                      </span>
                    </div>

                    <div style={{ display: 'grid', gap: '12px' }}>
                      {appointmentDrafts.map((draft, index) => (
                        <div
                          key={`appointment-draft-view-${index}`}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(10rem, 13rem) minmax(0, 1fr) auto',
                            gap: '12px',
                            alignItems: 'start'
                          }}
                        >
                          <select
                            value={draft.check_type}
                            onChange={(event) => updateAppointmentDraft(index, 'check_type', event.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '2px solid rgba(102, 126, 234, 0.2)',
                              borderRadius: '8px',
                              fontSize: '14px',
                              background: 'rgba(255, 248, 241, 0.88)',
                              color: '#1a202c',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">{isZh ? '选择检查类型' : 'Select check type'}</option>
                            <optgroup label={isZh ? '设备可测项目' : 'Device-measurable'}>
                              <option value="blood_pressure">{t('checkTypes.blood_pressure')}</option>
                              <option value="blood_glucose">{t('checkTypes.blood_glucose')}</option>
                              <option value="temperature">{t('checkTypes.temperature')}</option>
                              <option value="body_height_weight">{t('checkTypes.body_height_weight')}</option>
                              <option value="spo2">{t('checkTypes.spo2')}</option>
                              <option value="lipid_panel">{t('checkTypes.lipid_panel')}</option>
                              <option value="body_composition">{t('checkTypes.body_composition')}</option>
                              <option value="uric_acid">{t('checkTypes.uric_acid')}</option>
                              <option value="cholesterol">{t('checkTypes.cholesterol')}</option>
                              <option value="urine_glucose">{t('checkTypes.urine_glucose')}</option>
                            </optgroup>
                            <optgroup label={isZh ? '实验室 / 影像' : 'Lab / Imaging'}>
                              <option value="blood">{t('checkTypes.blood')}</option>
                              <option value="urine">{t('checkTypes.urine')}</option>
                              <option value="ecg">{t('checkTypes.ecg')}</option>
                              <option value="ct">{t('checkTypes.ct')}</option>
                              <option value="xray">{t('checkTypes.xray')}</option>
                              <option value="ultrasound">{t('checkTypes.ultrasound')}</option>
                              <option value="mri">{t('checkTypes.mri')}</option>
                              <option value="pathology">{t('checkTypes.pathology')}</option>
                              <option value="endoscopy">{t('checkTypes.endoscopy')}</option>
                              <option value="other">{t('checkTypes.other')}</option>
                            </optgroup>
                          </select>

                          <input
                            type="text"
                            value={draft.description}
                            onChange={(event) => updateAppointmentDraft(index, 'description', event.target.value)}
                            placeholder={t('reports.appointmentOrdersDescriptionPlaceholder')}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '2px solid rgba(102, 126, 234, 0.2)',
                              borderRadius: '8px',
                              fontSize: '14px',
                              background: 'rgba(255, 248, 241, 0.88)',
                              color: '#1a202c',
                              boxSizing: 'border-box',
                              outline: 'none',
                            }}
                          />

                          <button
                            type="button"
                            onClick={() => removeAppointmentDraft(index)}
                            disabled={creatingAppointmentOrders}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '8px',
                              border: '1px solid rgba(102, 126, 234, 0.3)',
                              background: 'transparent',
                              color: '#4a5568',
                              cursor: creatingAppointmentOrders ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              whiteSpace: 'nowrap',
                              opacity: creatingAppointmentOrders ? 0.5 : 1,
                            }}
                          >
                            {isZh ? '移除' : 'Remove'}
                          </button>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginTop: '16px' }}>
                      <button
                        type="button"
                        onClick={addAppointmentDraft}
                        disabled={creatingAppointmentOrders}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '999px',
                          border: '1px solid rgba(67, 214, 197, 0.4)',
                          background: 'linear-gradient(135deg, rgba(67, 214, 197, 0.2), rgba(67, 214, 197, 0.08))',
                          color: '#1a4a42',
                          fontWeight: '600',
                          cursor: creatingAppointmentOrders ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          opacity: creatingAppointmentOrders ? 0.5 : 1,
                        }}
                      >
                        {t('reports.addAppointmentOrder')}
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateAppointmentOrders}
                        disabled={creatingAppointmentOrders}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '999px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #d4a96a, #c8935a)',
                          color: '#0b1727',
                          fontWeight: '600',
                          cursor: creatingAppointmentOrders ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          opacity: creatingAppointmentOrders ? 0.5 : 1,
                        }}
                      >
                        {creatingAppointmentOrders ? t('reports.appointmentOrdersCreating') : t('reports.appointmentOrdersSubmit')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 报告内容 */}
        <div style={{
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '20px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: 0,
              color: '#2c3e50',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              {t('reports.reportContent')}
            </h3>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={handleExportMarkdown}
                disabled={!reportContent || reportContent.trim() === ''}
                style={{
                  ...getButtonStyle('secondary'),
                  opacity: (!reportContent || reportContent.trim() === '') ? 0.4 : 1,
                  cursor: (!reportContent || reportContent.trim() === '') ? 'not-allowed' : 'pointer',
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, true)}
                onMouseLeave={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, false)}
                title="导出为Markdown格式"
              >
                📥 MD
              </button>

              <button
                type="button"
                onClick={handleExportPDF}
                disabled={!reportContent || reportContent.trim() === ''}
                style={{
                  ...getButtonStyle('primary'),
                  opacity: (!reportContent || reportContent.trim() === '') ? 0.4 : 1,
                  cursor: (!reportContent || reportContent.trim() === '') ? 'not-allowed' : 'pointer',
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, true)}
                onMouseLeave={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, false)}
                title="导出为PDF格式"
              >
                📄 PDF
              </button>

              <button
                type="button"
                onClick={handleExportDOCX}
                disabled={!reportContent || reportContent.trim() === ''}
                style={{
                  ...getButtonStyle('success'),
                  opacity: (!reportContent || reportContent.trim() === '') ? 0.4 : 1,
                  cursor: (!reportContent || reportContent.trim() === '') ? 'not-allowed' : 'pointer',
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, true)}
                onMouseLeave={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, false)}
                title="导出为Word格式"
              >
                📝 Word
              </button>
            </div>
          </div>
          
          {reportContent ? (
            <div className="markdown-content markdown-content--paper">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {reportContent}
              </ReactMarkdown>
            </div>
          ) : (
            <p style={{ color: '#666', fontStyle: 'italic' }}>
              {t('reports.noContent')}
            </p>
          )}
        </div>

        {/* 检查项列表 */}
        <div style={{
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '20px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: '20px',
            color: '#2c3e50',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            {t('reports.checksList')}
          </h3>
          
          {checksLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <div style={{ 
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid rgba(0,0,0,.1)',
                borderRadius: '50%',
                borderTopColor: '#667eea',
                animation: 'spin 1s linear infinite',
                marginBottom: '20px'
              }}></div>
              <p style={{ fontSize: '1.1rem', color: '#666' }}>{t('reports.loadingChecks')}</p>
            </div>
          ) : checksError ? (
            <div style={{ 
              color: '#ff416c', 
              marginBottom: '20px', 
              textAlign: 'center',
              background: 'rgba(255, 65, 108, 0.1)',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 65, 108, 0.3)',
              fontWeight: '500'
            }}>
              {checksError}
            </div>
          ) : checks.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', fontSize: '1.1rem', padding: '50px' }}>
              {t('reports.noChecksData')}
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="paper-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ 
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                  }}>
                    <th style={{ padding: '15px', textAlign: 'left', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '30%', color: '#14253a' }}>
                      {t('reports.checkType')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '25%', color: '#14253a' }}>
                      {t('reports.createdAt')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '45%', color: '#14253a' }}>
                      {t('reports.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((check, index) => (
                    <React.Fragment key={check.id || index}>
                      <tr style={{ 
                        transition: 'background-color 0.3s ease',
                        '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.05)' }
                      }}>
                        <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(41, 128, 185, 0.1) 100%)',
                            color: '#2980b9',
                            border: '1px solid rgba(52, 152, 219, 0.3)'
                          }}>
                            {getCheckTypeDisplay(check.check_type)}
                          </span>
                        </td>
                        <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                          {formatDate(check.created_at)}
                        </td>
                        <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                          <button 
                            onClick={() => toggleCheckDetails(index)}
                            style={{
                              ...getButtonStyle('secondary'),
                              padding: '8px 16px',
                              fontSize: '14px',
                              minWidth: 'auto'
                            }}
                            onMouseEnter={(e) => handleButtonHover(e, true)}
                            onMouseLeave={(e) => handleButtonHover(e, false)}
                          >
                            {t('reports.viewDetails')}
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3" style={{ padding: '0' }}>
                          <div 
                            id={`check-details-${index}`}
                            style={{ 
                              display: 'none',
                              padding: '20px',
                              background: 'rgba(102, 126, 234, 0.02)',
                              borderTop: '1px solid rgba(102, 126, 234, 0.1)'
                            }}
                          >
                            {renderCheckMetadata(check)}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 待检查项目列表 */}
        <div style={{
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '20px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: '20px',
            color: '#e67e22',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            {t('reports.pendingChecksList')}
          </h3>
          
          {pendingChecksLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <div style={{ 
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid rgba(0,0,0,.1)',
                borderRadius: '50%',
                borderTopColor: '#f39c12',
                animation: 'spin 1s linear infinite',
                marginBottom: '20px'
              }}></div>
              <p style={{ fontSize: '1.1rem', color: '#666' }}>{t('reports.loadingPendingChecks')}</p>
            </div>
          ) : pendingChecksError ? (
            <div style={{ 
              color: '#ff416c', 
              marginBottom: '20px', 
              textAlign: 'center',
              background: 'rgba(255, 65, 108, 0.1)',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 65, 108, 0.3)',
              fontWeight: '500'
            }}>
              {pendingChecksError}
            </div>
          ) : pendingChecks.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', fontSize: '1.1rem', padding: '50px' }}>
              {t('reports.noPendingChecksData')}
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="paper-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ 
                    background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%)'
                  }}>
                    <th style={{ padding: '15px', textAlign: 'left', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '18%', color: '#14253a' }}>
                      {t('reports.checkType')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '12%', color: '#14253a' }}>
                      {t('reports.status')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '15%', color: '#14253a' }}>
                      {t('reports.createdAt')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '22%', color: '#14253a' }}>
                      {t('reports.description')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '15%', color: '#14253a' }}>
                      {t('reports.reviewInfo')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '18%', color: '#14253a' }}>
                      {t('reports.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pendingChecks.map((pendingCheck, index) => (
                    <tr key={pendingCheck.id || index} style={{ 
                      transition: 'background-color 0.3s ease',
                      '&:hover': { backgroundColor: 'rgba(243, 156, 18, 0.05)' }
                    }}>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        <span style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%)',
                          color: '#e67e22',
                          border: '1px solid rgba(243, 156, 18, 0.3)'
                        }}>
                          {getCheckTypeDisplay(pendingCheck.check_type)}
                        </span>
                      </td>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          ...(pendingCheck.status === 'pending' ? {
                            background: 'rgba(241, 196, 15, 0.1)',
                            color: '#f39c12',
                            border: '1px solid rgba(241, 196, 15, 0.3)'
                          } : pendingCheck.status === 'approved' ? {
                            background: 'rgba(46, 204, 113, 0.1)',
                            color: '#27ae60',
                            border: '1px solid rgba(46, 204, 113, 0.3)'
                          } : pendingCheck.status === 'completed' ? {
                            background: 'rgba(52, 152, 219, 0.1)',
                            color: '#3498db',
                            border: '1px solid rgba(52, 152, 219, 0.3)'
                          } : {
                            background: 'rgba(231, 76, 60, 0.1)',
                            color: '#e74c3c',
                            border: '1px solid rgba(231, 76, 60, 0.3)'
                          })
                        }}>
                          {t(`reports.pendingCheckStatus.${pendingCheck.status}`)}
                        </span>
                      </td>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        {formatDate(pendingCheck.created_at)}
                      </td>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        {pendingCheck.description || t('reports.noDescription')}
                      </td>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        {pendingCheck.status === 'pending' ? (
                          <div style={{ 
                            color: '#f39c12', 
                            fontSize: '14px',
                            fontStyle: 'italic',
                            background: 'rgba(241, 196, 15, 0.1)',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid rgba(241, 196, 15, 0.3)'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              {t('reports.pendingReviewNote')}
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: '#556476', fontSize: '14px' }}>
                            {pendingCheck.reviewer_name && (
                              <div>{t('reports.reviewedBy')}: {pendingCheck.reviewer_name}</div>
                            )}
                            {pendingCheck.reviewed_at && (
                              <div style={{ fontSize: '12px', marginTop: '4px', color: '#627487' }}>
                                {formatDate(pendingCheck.reviewed_at)}
                              </div>
                            )}
                            {pendingCheck.review_note && (
                              <div style={{ 
                                fontSize: '12px', 
                                marginTop: '4px', 
                                fontStyle: 'italic',
                                color: '#556476'
                              }}>
                                {t('reports.note')}: {pendingCheck.review_note}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        {pendingCheck.status === 'approved' ? (
                          <button
                            onClick={() => openResultModal(pendingCheck)}
                            style={{
                              ...getButtonStyle('success'),
                              padding: '8px 16px',
                              fontSize: '14px',
                              minWidth: 'auto'
                            }}
                            onMouseEnter={(e) => handleButtonHover(e, true)}
                            onMouseLeave={(e) => handleButtonHover(e, false)}
                          >
                            {t('reports.inputResult')}
                          </button>
                        ) : (
                          <span style={{ 
                            color: '#999', 
                            fontSize: '14px', 
                            fontStyle: 'italic' 
                          }}>
                            {pendingCheck.status === 'completed' ? t('reports.pendingCheckStatus.completed') : t('reports.notAvailable')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 对话消息列表 */}
        <div style={{
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          marginTop: '20px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              margin: 0,
              color: '#2c3e50',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              {t('reports.conversationHistory')}
            </h3>
            <button
              onClick={() => setShowConversations(!showConversations)}
              style={{
                ...getButtonStyle('secondary'),
                padding: '8px 16px',
                fontSize: '14px',
                minWidth: 'auto'
              }}
              onMouseEnter={(e) => handleButtonHover(e, true)}
              onMouseLeave={(e) => handleButtonHover(e, false)}
            >
              {showConversations ? t('reports.hideConversation') : t('reports.showConversation')}
            </button>
          </div>

          {showConversations && (
            <div>
              {messagesLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <div style={{ 
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid rgba(0,0,0,.1)',
                borderRadius: '50%',
                borderTopColor: '#667eea',
                animation: 'spin 1s linear infinite',
                marginBottom: '20px'
              }}></div>
              <p style={{ fontSize: '1.1rem', color: '#666' }}>{t('reports.loadingConversation')}</p>
            </div>
          ) : messagesError ? (
            <div style={{ 
              color: '#ff416c', 
              marginBottom: '20px', 
              textAlign: 'center',
              background: 'rgba(255, 65, 108, 0.1)',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 65, 108, 0.3)',
              fontWeight: '500'
            }}>
              {messagesError}
            </div>
          ) : messages.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', fontSize: '1.1rem', padding: '50px' }}>
              {t('reports.noConversationData')}
            </p>
          ) : (
            <div style={{
              maxHeight: '600px',
              overflowY: 'auto',
              padding: '20px',
              background: 'rgba(248, 249, 250, 0.8)',
              borderRadius: '12px',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              {messages.map((message, index) => renderConversationMessage(message, index))}
            </div>
          )}
            </div>
          )}
        </div>
      </div>

      {/* 检查结果输入模态框 */}
      {showResultModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                margin: 0,
                color: '#2c3e50',
                fontSize: '1.5rem',
                fontWeight: '600'
              }}>
                {t('reports.inputCheckResult')}
              </h3>
              <button
                onClick={closeResultModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999',
                  padding: '5px'
                }}
              >
                ✕
              </button>
            </div>

            {selectedPendingCheck && (
              <div style={{
                marginBottom: '20px',
                padding: '15px',
                backgroundColor: 'rgba(52, 152, 219, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(52, 152, 219, 0.3)'
              }}>
                <div style={{ fontWeight: '600', color: '#2980b9', marginBottom: '5px' }}>
                  {selectedPendingCheck.description}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {t('reports.checkType')}: {getCheckTypeDisplay(selectedPendingCheck.check_type)}
                </div>
              </div>
            )}

            {submitError && (
              <div style={{
                backgroundColor: 'rgba(231, 76, 60, 0.1)',
                border: '1px solid rgba(231, 76, 60, 0.3)',
                color: '#e74c3c',
                padding: '10px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                {submitError}
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                {t('reports.inputType')}
              </label>
              <select
                value={resultInputType}
                onChange={(e) => setResultInputType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              >
                <option value="text">{t('reports.freeText')}</option>
                <option value="select">{t('reports.selectResult')}</option>
              </select>
            </div>

            {resultInputType === 'text' ? (
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#2c3e50'
                }}>
                  {t('reports.checkResult')}
                </label>
                <textarea
                  value={resultText}
                  onChange={(e) => setResultText(e.target.value)}
                  placeholder={t('reports.enterCheckResult')}
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    color: '#1a202c'
                  }}
                />
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#2c3e50'
                }}>
                  {t('reports.checkResult')}
                </label>
                <select
                  value={resultValue}
                  onChange={(e) => setResultValue(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                >
                  <option value="阴性">{t('reports.negative')}</option>
                  <option value="阳性">{t('reports.positive')}</option>
                  <option value="正常">{t('reports.normal')}</option>
                  <option value="异常">{t('reports.abnormal')}</option>
                </select>
              </div>
            )}

            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                {t('reports.additionalDescription')} ({t('reports.optional')})
              </label>
              <textarea
                value={resultDescription}
                onChange={(e) => setResultDescription(e.target.value)}
                placeholder={t('reports.enterAdditionalInfo')}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  color: '#1a202c'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={closeResultModal}
                disabled={submittingResult}
                style={{
                  ...getButtonStyle('dark'),
                  padding: '12px 24px'
                }}
                onMouseEnter={(e) => !submittingResult && handleButtonHover(e, true)}
                onMouseLeave={(e) => !submittingResult && handleButtonHover(e, false)}
              >
                {t('common.cancel')}
              </button>
              
              <button
                onClick={submitResult}
                disabled={submittingResult || (!resultText.trim() && resultInputType === 'text') || (!resultValue && resultInputType === 'select')}
                style={{
                  ...getButtonStyle('success'),
                  padding: '12px 24px',
                  opacity: submittingResult || (!resultText.trim() && resultInputType === 'text') || (!resultValue && resultInputType === 'select') ? 0.6 : 1
                }}
                onMouseEnter={(e) => !submittingResult && handleButtonHover(e, true)}
                onMouseLeave={(e) => !submittingResult && handleButtonHover(e, false)}
              >
                {submittingResult ? t('reports.submitting') : t('reports.submitResult')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportView;
